import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, Timeframe } from 'webapp-types';
import { TimeframeType } from 'webapp/enums/enums';

import PopupInnerContextComponent, { type Args } from 'webapp/components/popup/inner/context';

export default abstract class CommonTimeframePopupIndexComponent extends PopupInnerContextComponent<PopupData.Common.TimeframeContext> {
	@service('timeframe') declare timeframeService: Service.Timeframe;

	@tracked currentYear: number; // the visible one

	@tracked selected: Timeframe | null | undefined;

	@tracked yearDateLabel = '';
	private yearDateLabelPromise: Promise<string> | null = null;

	@tracked periodsLoaded = false;

	// for template
	typeYear = TimeframeType.Y;
	typeQ1 = TimeframeType.Q1;
	typeQ2 = TimeframeType.Q2;
	typeQ3 = TimeframeType.Q3;
	typeQ4 = TimeframeType.Q4;
	typeH1 = TimeframeType.H1;
	typeH2 = TimeframeType.H2;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selected = this.popupData.selected;
		this.currentYear = this.selected?.year || new Date().getFullYear();

		this.updateYearData();
	}

	get disablePast() {
		return !!this.popupData.disablePast;
	}

	get isPrevArrowDisabled() {
		return this.disablePast && this.isYearDisabled;
	}

	get isYearDisabled() {
		return this.isTimeframeTypeDisabled(TimeframeType.Y);
	}

	get isH1Disabled() {
		return this.isTimeframeTypeDisabled(TimeframeType.H1);
	}

	get isH2Disabled() {
		return this.isTimeframeTypeDisabled(TimeframeType.H2);
	}

	get isQ1Disabled() {
		return this.isTimeframeTypeDisabled(TimeframeType.Q1);
	}

	get isQ2Disabled() {
		return this.isTimeframeTypeDisabled(TimeframeType.Q2);
	}

	get isQ3Disabled() {
		return this.isTimeframeTypeDisabled(TimeframeType.Q3);
	}

	get isQ4Disabled() {
		return this.isTimeframeTypeDisabled(TimeframeType.Q4);
	}

	private isTimeframeTypeDisabled(type: TimeframeType): boolean {
		if (!this.disablePast) {
			return false;
		}
		if (!this.periodsLoaded) {
			return true;
		}
		const periodsData = this.timeframeService.getTimeframePeriodsFromCache({
			type,
			year: this.currentYear,
		});
		if (!periodsData) {
			return true;
		}
		const item = periodsData.find((entry) => entry.timeframeType === type);
		return item ? !item.isActive : true;
	}

	@action
	changeSelected(type: TimeframeType | null, year: number) {
		const timeframe = type !== null ? { type, year } : null;
		this.selected = timeframe;
	}

	@action
	goPrev() {
		if (this.isPrevArrowDisabled) {
			return;
		}
		this.currentYear--;
		this.updateYearData();
	}

	@action
	goNext() {
		this.currentYear++;
		this.updateYearData();
	}

	private updateYearData() {
		this.yearDateLabel = '';
		this.periodsLoaded = false;

		const timeframe = {
			type: TimeframeType.Y,
			year: this.currentYear,
		};

		const promise = this.timeframeService.getTimeframePeriodsLabel(timeframe, 'MMM YYYY');
		void promise.then((label) => {
			if (this.yearDateLabelPromise === promise) {
				this.yearDateLabel = label;
				this.yearDateLabelPromise = null;
				this.periodsLoaded = true;
			}
		});
		this.yearDateLabelPromise = promise;
	}

	@action
	apply() {
		if (this.selected !== undefined) {
			this.popupData.onChange(this.selected);
		}
		this.close();
	}

	@action
	clear() {
		this.popupData.onClear?.();
		this.close();
	}
}
